import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowsLeftRight, MagnifyingGlass, Check, X, ArrowRight, Minus } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";

const fmt = (n) => (!n || n === 0) ? "$0" : `$${Number(n).toLocaleString()}`;

const piaLabels = { not_started: "Not Started", in_review: "In Review", info_requested: "Info Requested", approved: "Approved", denied: "Denied" };
const piaColors = { not_started: "bg-gray-100 text-[#6B7280]", in_review: "bg-[#002FA7]/10 text-[#002FA7]", info_requested: "bg-[#FFD100]/10 text-[#946B00]", approved: "bg-[#00B341]/10 text-[#00B341]", denied: "bg-[#FF2A00]/10 text-[#FF2A00]" };

const StepDot = ({ active, status }) => {
  if (!active) return <div className="w-8 h-8 border-2 border-gray-200 flex items-center justify-center bg-gray-50"><Minus size={14} className="text-gray-300" /></div>;
  const colors = {
    approved: "bg-[#00B341] border-[#00B341]", committed: "bg-[#00B341] border-[#00B341]",
    paid: "bg-[#002FA7] border-[#002FA7]", pending: "bg-[#FFD100] border-[#FFD100]",
    denied: "bg-[#FF2A00] border-[#FF2A00]", rejected: "bg-[#FF2A00] border-[#FF2A00]",
  };
  const color = colors[status] || "bg-[#002FA7] border-[#002FA7]";
  return (
    <div className={`w-8 h-8 border-2 ${color} flex items-center justify-center`}>
      <Check size={14} className="text-white" weight="bold" />
    </div>
  );
};

const StepLine = ({ active }) => (
  <div className={`flex-1 h-0.5 ${active ? "bg-[#002FA7]" : "bg-gray-200"}`} />
);

export default function FRNLifecycle() {
  const [frns, setFrns] = useState([]);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [selectedFrn, setSelectedFrn] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadFrns(); }, []);

  const loadFrns = async () => {
    try {
      const { data } = await api.get("/frn-lifecycle");
      setFrns(data);
      // Auto-open FRN detail if passed in URL
      const frnParam = searchParams.get("frn");
      if (frnParam) {
        openDetail(frnParam);
        setSearchParams({}, { replace: true });
      }
    }
    catch { toast.error("Failed to load FRN data"); }
  };

  const openDetail = async (frn) => {
    try {
      const { data } = await api.get(`/frn-lifecycle/${frn}`);
      setDetail(data);
      setSelectedFrn(frn);
      setDetailOpen(true);
    } catch { toast.error("Failed to load FRN details"); }
  };

  const filtered = frns
    .filter((f) => yearFilter === "all" || String(f.funding_year) === yearFilter)
    .filter((f) => JSON.stringify(f).toLowerCase().includes(search.toLowerCase()));

  const availableYears = [...new Set(frns.map((f) => f.funding_year).filter(Boolean))].sort().reverse();

  return (
    <div className="p-6" data-testid="frn-lifecycle-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">E-Rate Forms</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>FRN Lifecycle</h1>
        <p className="text-sm text-[#6B7280] mt-1">Track FRN progression: Form 470 &rarr; 471 &rarr; 486 &rarr; 472 (BEAR) / 474 (SPI)</p>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-frn" placeholder="Search FRN, client..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger data-testid="filter-lifecycle-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {availableYears.map((y) => <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-[#6B7280]">{filtered.length} FRNs</span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <ArrowsLeftRight size={32} className="mx-auto text-[#6B7280] mb-3" />
          <p className="text-sm text-[#6B7280]">No FRN records found. Add Form 471 applications with FRN numbers to track their lifecycle.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((f, idx) => (
            <button
              key={`${f.frn}-${idx}`}
              data-testid={`frn-row-${f.frn}`}
              onClick={() => openDetail(f.frn)}
              className="w-full bg-white border border-gray-200 p-5 text-left hover:border-[#002FA7]/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <span className="font-mono text-sm font-bold text-[#002FA7]">FRN {f.frn}</span>
                  <span className="text-sm text-[#1A1D20]">{f.client_name}</span>
                  {f.funding_year && <Badge variant="outline" className="rounded-none text-xs">FY{f.funding_year}</Badge>}
                  {f.form470_app && <Badge variant="outline" className="rounded-none text-xs font-mono">470: {f.form470_app}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  {f.pia_status && (
                    <Badge className={`rounded-none text-xs border ${piaColors[f.pia_status] || piaColors.not_started}`}>
                      PIA: {piaLabels[f.pia_status] || f.pia_status}
                    </Badge>
                  )}
                  {f.total_funding_request > 0 && <span className="text-sm font-medium">{fmt(f.total_funding_request)}</span>}
                </div>
              </div>

              {/* Lifecycle steps */}
              <div className="flex items-center gap-0">
                <div className="flex flex-col items-center gap-1">
                  <StepDot active={f.has_470} status={f.form470_status} />
                  <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">470</span>
                </div>
                <StepLine active={f.has_470 && f.has_471} />
                <div className="flex flex-col items-center gap-1">
                  <StepDot active={f.has_471} status={f.status_471} />
                  <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">471</span>
                </div>
                <StepLine active={f.has_471 && f.pia_status && f.pia_status !== "not_started"} />
                <div className="flex flex-col items-center gap-1">
                  <StepDot active={f.pia_status && f.pia_status !== "not_started"} status={f.pia_status === "approved" ? "approved" : f.pia_status === "denied" ? "denied" : f.pia_status === "in_review" ? "pending" : f.pia_status === "info_requested" ? "pending" : ""} />
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${f.pia_status === "approved" ? "text-[#00B341]" : f.pia_status === "denied" ? "text-[#FF2A00]" : "text-[#6B7280]"}`}>PIA</span>
                </div>
                <StepLine active={(f.pia_status === "approved" || f.status_471 === "approved" || f.status_471 === "committed") && f.has_486} />
                <div className="flex flex-col items-center gap-1">
                  <StepDot active={f.has_486} status={f.status_486} />
                  <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">486</span>
                </div>
                <StepLine active={f.has_486 && (f.has_472 || f.has_474)} />
                <div className="flex flex-col items-center gap-1">
                  <StepDot active={f.has_472} status={f.status_472} />
                  <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">472</span>
                </div>
                <div className="w-2" />
                <div className="flex flex-col items-center gap-1">
                  <StepDot active={f.has_474} status={f.status_474} />
                  <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">474</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="rounded-none sm:max-w-[750px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              FRN {selectedFrn} Lifecycle Detail
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6 py-4">
              {/* USAC Disbursement Summary */}
              {detail.usac_disbursement && (
                <div className="border-2 border-[#002FA7]/20 bg-[#002FA7]/5 p-4">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#002FA7]">USAC Disbursement Summary</span>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold">Invoicing Method</span>
                      <p className="text-lg font-bold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{detail.usac_disbursement.invoicing_mode || "—"}</p>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold">Authorized Disbursement</span>
                      <p className="text-lg font-bold text-[#00B341]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmt(detail.usac_disbursement.total_authorized_disbursement)}</p>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold">USAC FRN Status</span>
                      <p className="text-lg font-bold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{detail.usac_disbursement.frn_status || "—"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Form 470 */}
              <div className="border border-gray-200">
                <div className="bg-[#F8F9FA] px-4 py-2 border-b border-gray-200">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Form 470 - Services Requested ({(detail.form470 || []).length})</span>
                </div>
                {(detail.form470 || []).length === 0 ? (
                  <p className="p-4 text-sm text-[#6B7280]">No Form 470 records linked</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs py-2 px-3">App #</TableHead>
                        <TableHead className="text-xs py-2 px-3">Services</TableHead>
                        <TableHead className="text-xs py-2 px-3">Posted</TableHead>
                        <TableHead className="text-xs py-2 px-3">Bids</TableHead>
                        <TableHead className="text-xs py-2 px-3">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.form470.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-2 px-3 font-mono text-xs">{r.application_number || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm max-w-[200px] truncate">{r.services_requested || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm">{r.posting_date || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm">{r.bid_count || 0}</TableCell>
                          <TableCell className="py-2 px-3 capitalize text-sm">{r.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Form 471 */}
              <div className="border border-gray-200">
                <div className="bg-[#F8F9FA] px-4 py-2 border-b border-gray-200">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Form 471 - Application ({detail.form471.length})</span>
                </div>
                {detail.form471.length === 0 ? (
                  <p className="p-4 text-sm text-[#6B7280]">No Form 471 records</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs py-2 px-3">App #</TableHead>
                        <TableHead className="text-xs py-2 px-3">Client</TableHead>
                        <TableHead className="text-xs py-2 px-3">Amount</TableHead>
                        <TableHead className="text-xs py-2 px-3">PIA Status</TableHead>
                        <TableHead className="text-xs py-2 px-3">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.form471.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-2 px-3 font-mono text-xs">{r.application_number || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm">{r.client_name || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm font-medium">{fmt(r.total_funding_request)}</TableCell>
                          <TableCell className="py-2 px-3"><Badge className={`rounded-none text-xs ${piaColors[r.pia_status] || ""}`}>{piaLabels[r.pia_status] || r.pia_status}</Badge></TableCell>
                          <TableCell className="py-2 px-3 capitalize text-sm">{r.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Form 486 */}
              <div className="border border-gray-200">
                <div className="bg-[#F8F9FA] px-4 py-2 border-b border-gray-200">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Form 486 - Receipt of Service ({detail.form486.length})</span>
                </div>
                {detail.form486.length === 0 ? (
                  <p className="p-4 text-sm text-[#6B7280]">No Form 486 records</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs py-2 px-3">Client</TableHead>
                        <TableHead className="text-xs py-2 px-3">Provider</TableHead>
                        <TableHead className="text-xs py-2 px-3">Service Start</TableHead>
                        <TableHead className="text-xs py-2 px-3">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.form486.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-2 px-3 text-sm">{r.client_name || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm">{r.service_provider || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm">{r.service_start_date || "—"}</TableCell>
                          <TableCell className="py-2 px-3 capitalize text-sm">{r.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Form 472 */}
              <div className="border border-gray-200">
                <div className="bg-[#F8F9FA] px-4 py-2 border-b border-gray-200">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Form 472 BEAR - Reimbursement ({detail.form472.length})</span>
                </div>
                {detail.form472.length === 0 ? (
                  <p className="p-4 text-sm text-[#6B7280]">No Form 472 records</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs py-2 px-3">Invoice #</TableHead>
                        <TableHead className="text-xs py-2 px-3">Amount</TableHead>
                        <TableHead className="text-xs py-2 px-3">Disbursed</TableHead>
                        <TableHead className="text-xs py-2 px-3">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.form472.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-2 px-3 text-sm">{r.invoice_number || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm font-medium">{fmt(r.invoice_amount)}</TableCell>
                          <TableCell className="py-2 px-3 text-sm text-[#00B341]">{fmt(r.disbursement_amount)}</TableCell>
                          <TableCell className="py-2 px-3 capitalize text-sm">{r.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Form 474 */}
              <div className="border border-gray-200">
                <div className="bg-[#F8F9FA] px-4 py-2 border-b border-gray-200">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Form 474 SPI - Provider Invoice ({detail.form474.length})</span>
                </div>
                {detail.form474.length === 0 ? (
                  <p className="p-4 text-sm text-[#6B7280]">No Form 474 records</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs py-2 px-3">Invoice #</TableHead>
                        <TableHead className="text-xs py-2 px-3">Amount</TableHead>
                        <TableHead className="text-xs py-2 px-3">Auth. Disbursement</TableHead>
                        <TableHead className="text-xs py-2 px-3">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.form474.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-2 px-3 text-sm">{r.invoice_number || "—"}</TableCell>
                          <TableCell className="py-2 px-3 text-sm font-medium">{fmt(r.invoice_amount)}</TableCell>
                          <TableCell className="py-2 px-3 text-sm text-[#00B341]">{fmt(r.authorized_disbursement)}</TableCell>
                          <TableCell className="py-2 px-3 capitalize text-sm">{r.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
